/**
 * N.4 — Şablon Yönetimi (super_admin)
 * A4/A5, dikey/yatay, tablo + metin alan seçimi, canlı önizleme
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const tkn = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Tablo satırları (her zaman belge üstündeki kutucuklu tabloda gösterilir)
const TABLO_ALANLARI = [
  { key: 'birim',          label: 'Birim',                zorunlu: true  },
  { key: 'konu',           label: 'Konu',                 zorunlu: true  },
  { key: 'sunulan_makam',  label: 'Sunulan Makam',        zorunlu: false },
  { key: 'hazirlayan',     label: 'Hazırlayan',           zorunlu: false },
  { key: 'tarih',          label: 'Tarihi',               zorunlu: false },
  { key: 'yonetici_ozeti', label: 'Kısa Yönetici Özeti', zorunlu: false },
];

// Ana metin alanları (tablonun altında serbest metin olarak)
const METIN_ALANLARI = [
  { key: 'ana_metin',         label: 'Ana Metin'              },
  { key: 'istatistiki_bilgi', label: 'İstatistiki Bilgiler'   },
  { key: 'sonuc',             label: 'Sonuç ve Değerlendirme' },
];

const ÖRNEK: Record<string, string> = {
  birim:             'Strateji Geliştirme Dairesi Başkanlığı',
  konu:              'Yıllık Faaliyet Raporu Hakkında',
  sunulan_makam:     'Genel Müdür',
  hazirlayan:        'Ad Soyad / Uzman',
  tarih:             new Date().toLocaleDateString('tr-TR'),
  yonetici_ozeti:    'Konunun özü kısaca bu alanda aktarılır. İki-üç cümleyi geçmeyen yönetici özeti.',
  ana_metin:         'Konunun ayrıntılı açıklaması bu alanda yer alır. Birden fazla paragraf içerebilir.',
  istatistiki_bilgi: 'Sayısal veriler, tablolar ve grafikler bu alanda gösterilir.',
  sonuc:             'Öneriler ve çıkarımlar bu bölümde aktarılır.',
};

// Varsayılan form değerleri
const emptyForm = () => ({
  ad:           '',
  aciklama:     '',
  sayfa_boyutu: 'A4',
  yonelim:      'dikey',
  // renkli=false → beyaz zemin/siyah yazı/gri çizgi (belge varsayılanı)
  renkli:       false,
  renk_zemin:   '#1a4a7a',
  renk_yazi:    '#ffffff',
  renk_cizgi:   '#555555',
  alan_sirasi:  ['birim', 'konu', 'sunulan_makam', 'hazirlayan', 'tarih', 'yonetici_ozeti', 'ana_metin'],
});

// Önizleme renk hesaplayıcı — renkli=false ise belge standarı (siyah/beyaz/gri)
function hesaplaRenkler(form: any) {
  if (!form.renkli) {
    return {
      labelBg:      '#ffffff',
      labelText:    '#000000',
      border:       '#666666',
      headerLine:   '#888888',
      headingColor: '#333333',  // metin alan başlıkları
    };
  }
  const zemin = form.renk_zemin || '#1a4a7a';
  return {
    labelBg:      zemin,
    labelText:    form.renk_yazi  || '#ffffff',
    border:       form.renk_cizgi || '#555555',
    headerLine:   form.renk_cizgi || '#555555',
    headingColor: zemin,
  };
}

// Seçili alanları form'dan türet
function secilenTabloAlanlari(alan_sirasi: string[]) {
  return TABLO_ALANLARI.filter(a => a.zorunlu || alan_sirasi.includes(a.key));
}
function secilenMetinAlanlari(alan_sirasi: string[]) {
  return METIN_ALANLARI.filter(a => alan_sirasi.includes(a.key));
}

// Sayfa önizlemesi
// Gerçek belge ölçüleri (DXA):
//   Dikey A4 : sayfa 11906×16838, tablo label %26.5, satır h=540, yönetici_ozeti h=1203, tarih h=438
//   Yatay A5 : sayfa 11906×8391,  tablo label %15.1, 3 satır: birim/konu/açıklama (h=540,540,1203)
//   Dikey A5 : sayfa 8391×11906,  label %26.5 (A4 ile aynı oran)
//   Yatay A4 : sayfa 16838×11906, label %15 (geniş yatay format)
function SayfaOnizleme({ form, maxW = 440 }: { form: any; maxW?: number }) {
  const boyut   = form.sayfa_boyutu || 'A4';
  const yonelim = form.yonelim      || 'dikey';
  const alanlar: string[] = form.alan_sirasi || [];
  const { labelBg, labelText, border, headerLine, headingColor } = hesaplaRenkler(form);

  const isA4        = boyut === 'A4';
  const isLandscape = yonelim === 'yatay';

  // Referans pt boyutları (1pt = 1.333px)
  const refW_port = isA4 ? 595 : 420;
  const refH_port = isA4 ? 842 : 595;
  const refW = isLandscape ? refH_port : refW_port;
  const refH = isLandscape ? refW_port : refH_port;

  // Etiket sütunu oranı: yatay formatta %15, dikide %27
  const labelPct = isLandscape ? 15 : 27;

  // Önizleme alanına sığdır
  const MAX_W = maxW;
  const scale = MAX_W / refW;
  const pageW = Math.round(refW * scale);
  const pageH = Math.round(refH * scale);

  // Margin: v3_A4 → left/right 1134 DXA ≈ 2cm, v1_A5 → 720 DXA ≈ 1.27cm
  const marginPt = isLandscape ? 36 : 48;
  const margin   = Math.round(marginPt * scale);

  const fs = {
    title: Math.max(6, Math.round(10 * scale)),
    label: Math.max(5, Math.round(7.5 * scale)),
    value: Math.max(4, Math.round(7 * scale)),
    body:  Math.max(4, Math.round(6.5 * scale)),
  };

  // Satır yükseklikleri (DXA → pt dönüşüm: 1 DXA = 0.05pt → gerçek: 540 DXA ≈ 9.5mm)
  const rowH    = Math.round(18 * scale);  // standart (540 DXA)
  const shortH  = Math.round(14 * scale);  // tarih satırı (438 DXA)
  const bigRowH = Math.round(50 * scale);  // yönetici özeti (1203 DXA)

  const borderPx = Math.max(1, Math.round(1.5 * scale));

  // Logo boyutu: header satır yüksekliği 1197 DXA ≈ 2.1cm, logo 722630 EMU ≈ 2.0cm
  // Ölçeklenmiş logo yüksekliği = 2cm * scale
  const logoH = Math.round(48 * scale);   // header satır yüksekliği
  const logoImgH = Math.round(38 * scale); // logo görseli (biraz küçük)

  // Header: 3 sütun, kenarsız tablo — sol logo | orta BİLGİ NOTU | sağ logo
  // Gerçek oranlar: 1356 / 9638 ≈ %14, 6926 / 9638 ≈ %72
  const PageHeader = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '14% 72% 14%',
      alignItems: 'center',
      height: logoH,
      marginBottom: Math.round(4 * scale),
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <img src="/tigem_logo.jpeg" alt="TİGEM" style={{ height: logoImgH, objectFit: 'contain' }} />
      </div>
      <div style={{ textAlign: 'center' }}>
        <span style={{
          fontFamily: 'Times New Roman, serif',
          fontWeight: 700,
          fontSize: Math.max(8, Math.round(16 * scale)),
          color: '#000',
          letterSpacing: 1,
        }}>
          BİLGİ NOTU
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <img src="/tigem_logo.jpeg" alt="TİGEM" style={{ height: logoImgH, objectFit: 'contain' }} />
      </div>
    </div>
  );

  // Yatay format: sadece birim, konu, açıklama (3 satır)
  if (isLandscape) {
    const aciklamaOlabilir = alanlar.some(k => ['yonetici_ozeti', 'ana_metin', 'istatistiki_bilgi', 'sonuc'].includes(k));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0' }}>
        <div style={{ width: pageW, minHeight: pageH, background: '#fff', boxShadow: '2px 4px 16px rgba(0,0,0,0.28)', fontFamily: 'Arial, sans-serif' }}>
          <div style={{ padding: margin }}>
            <PageHeader />
            <div style={{ borderTop: `${borderPx}px solid ${headerLine}`, marginBottom: Math.round(7 * scale) }} />
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: `${labelPct}%` }} />
                <col style={{ width: `${100 - labelPct}%` }} />
              </colgroup>
              <tbody>
                {/* BİRİM */}
                <tr>
                  <td style={{ background: labelBg, color: labelText, fontSize: fs.label, fontWeight: 700, padding: `${Math.round(2*scale)}px ${Math.round(3*scale)}px`, verticalAlign: 'top', height: rowH, border: `1px solid ${border}` }}>
                    BİRİM
                  </td>
                  <td style={{ fontSize: fs.value, color: '#444', padding: `${Math.round(2*scale)}px ${Math.round(4*scale)}px`, verticalAlign: 'top', height: rowH, border: `1px solid ${border}`, fontStyle: 'italic' }}>
                    {ÖRNEK.birim}
                  </td>
                </tr>
                {/* KONU */}
                <tr>
                  <td style={{ background: labelBg, color: labelText, fontSize: fs.label, fontWeight: 700, padding: `${Math.round(2*scale)}px ${Math.round(3*scale)}px`, verticalAlign: 'top', height: rowH, border: `1px solid ${border}` }}>
                    KONU
                  </td>
                  <td style={{ fontSize: fs.value, color: '#444', padding: `${Math.round(2*scale)}px ${Math.round(4*scale)}px`, verticalAlign: 'top', height: rowH, border: `1px solid ${border}`, fontStyle: 'italic' }}>
                    {ÖRNEK.konu}
                  </td>
                </tr>
                {/* AÇIKLAMA — yatay formatta tüm içerik tek alanda */}
                {aciklamaOlabilir && (
                  <tr>
                    <td style={{ background: labelBg, color: labelText, fontSize: fs.label, fontWeight: 700, padding: `${Math.round(2*scale)}px ${Math.round(3*scale)}px`, verticalAlign: 'top', height: bigRowH, border: `1px solid ${border}` }}>
                      AÇIKLAMA
                    </td>
                    <td style={{ fontSize: fs.value, color: '#444', padding: `${Math.round(2*scale)}px ${Math.round(4*scale)}px`, verticalAlign: 'top', height: bigRowH, border: `1px solid ${border}`, fontStyle: 'italic', lineHeight: 1.4 }}>
                      {ÖRNEK.yonetici_ozeti} {ÖRNEK.ana_metin}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#888', marginTop: 6, textAlign: 'center' }}>
          {boyut} Yatay · etiket %{labelPct} · {pageW}×{pageH}px
        </div>
      </div>
    );
  }

  // Dikey format: tam tablo + metin alanları
  const tabloSatirlar = secilenTabloAlanlari(alanlar);
  const metinAlanlar  = secilenMetinAlanlari(alanlar);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0' }}>
      <div style={{ width: pageW, minHeight: pageH, background: '#fff', boxShadow: '2px 4px 16px rgba(0,0,0,0.28)', fontFamily: 'Arial, sans-serif', overflow: 'hidden' }}>
        <div style={{ padding: margin }}>
          {/* Header: logo — BİLGİ NOTU — logo (gerçek belge yapısı) */}
          <PageHeader />
          <div style={{ borderTop: `${borderPx}px solid ${headerLine}`, marginBottom: Math.round(8 * scale) }} />

          {/* 2 sütunlu tablo — label %27, değer %73 (gerçek belge: 2548/9628=26.5%) */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: Math.round(10 * scale) }}>
            <colgroup>
              <col style={{ width: `${labelPct}%` }} />
              <col style={{ width: `${100 - labelPct}%` }} />
            </colgroup>
            <tbody>
              {tabloSatirlar.map(alan => {
                const isTall  = alan.key === 'yonetici_ozeti';
                const isShort = alan.key === 'tarih';
                const h = isTall ? bigRowH : (isShort ? shortH : rowH);
                return (
                  <tr key={alan.key}>
                    <td style={{ background: labelBg, color: labelText, fontSize: fs.label, fontWeight: 700, padding: `${Math.round(2*scale)}px ${Math.round(4*scale)}px`, verticalAlign: 'top', height: h, border: `1px solid ${border}`, lineHeight: 1.3 }}>
                      {alan.key === 'tarih' ? 'BİLGİ NOTU TARİHİ' : alan.label.toUpperCase()}
                    </td>
                    <td style={{ fontSize: fs.value, color: '#444', padding: `${Math.round(2*scale)}px ${Math.round(5*scale)}px`, verticalAlign: 'top', height: h, border: `1px solid ${border}`, fontStyle: 'italic', lineHeight: 1.4 }}>
                      {ÖRNEK[alan.key]}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Metin alanları */}
          {metinAlanlar.map(alan => (
            <div key={alan.key} style={{ marginBottom: Math.round(8 * scale) }}>
              <div style={{ fontSize: fs.label, fontWeight: 700, color: headingColor, letterSpacing: 0.3, marginBottom: Math.round(2 * scale) }}>
                {alan.key === 'istatistiki_bilgi' ? 'KONUYA İLİŞKİN İSTATİSTİKİ BİLGİLER' : alan.label.toUpperCase()}
              </div>
              <div style={{ fontSize: fs.body, color: '#999', fontStyle: 'italic', lineHeight: 1.5 }}>
                {ÖRNEK[alan.key]}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 10, color: '#888', marginTop: 6, textAlign: 'center' }}>
        {boyut} Dikey · etiket %{labelPct} · {pageW}×{pageH}px
      </div>
    </div>
  );
}

// Şablon kartı (listede gösterilir)
function SablonKarti({ s, onDuzenle, onSil, onOnizle }: any) {
  const tabloCount  = secilenTabloAlanlari(s.alan_sirasi || []).length;
  const metinCount  = secilenMetinAlanlari(s.alan_sirasi || []).length;
  const kartiRenk   = s.renkli ? (s.renk_zemin || '#1a4a7a') : '#444444';
  return (
    <div style={{ border: `2px solid ${kartiRenk}33`, borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <div style={{ background: kartiRenk, color: '#fff', padding: '10px 14px' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{s.ad}</div>
        <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
          {s.sayfa_boyutu} · {s.yonelim === 'yatay' ? 'Yatay' : 'Dikey'} · {s.renkli ? '🎨 Renkli' : '⬜ Standart'}
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        {s.aciklama && <p style={{ margin: '0 0 8px', color: '#666', fontSize: 12 }}>{s.aciklama}</p>}
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
          Tablo: {tabloCount} satır · Metin: {metinCount} alan
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onOnizle(s)}
            style={{ background: '#f5f0ff', color: '#6c3fc5', border: '1px solid #d4b8f5', borderRadius: 5, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
            Önizle
          </button>
          <button onClick={() => onDuzenle(s)}
            style={{ flex: 1, background: '#eaf3fb', color: '#1a4a7a', border: '1px solid #bdd', borderRadius: 5, padding: '5px', cursor: 'pointer', fontSize: 12 }}>
            Düzenle
          </button>
          <button onClick={() => onSil(s)}
            style={{ background: '#fdecea', color: '#c0392b', border: '1px solid #f5c6c6', borderRadius: 5, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
            Sil
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ana bileşen ───────────────────────────────────────────────
export default function BilgiNotSablon() {
  const userStr  = localStorage.getItem('user');
  const user     = userStr ? JSON.parse(userStr) : null;
  const isAdmin  = user?.rol === 'super_admin';

  const [sablonlar, setSablonlar]   = useState<any[]>([]);
  const [form, setForm]             = useState<any>(emptyForm());
  const [secili, setSecili]         = useState<any>(null);
  const [modal, setModal]           = useState(false);
  const [onizlemeItem, setOnizleme] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata]             = useState('');
  const [mesaj, setMesaj]           = useState('');

  const yukle = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/bilgi-notu/sablonlar`, { headers: tkn() });
      setSablonlar(r.data);
    } catch { setSablonlar([]); }
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  if (!isAdmin) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
      Bu sayfaya erişim yalnızca sistem yöneticilerine aittir.
    </div>
  );

  const yeniAc = () => { setSecili(null); setForm(emptyForm()); setHata(''); setModal(true); };

  const duzenleAc = (s: any) => {
    setSecili(s);
    setForm({
      ad:           s.ad,
      aciklama:     s.aciklama    || '',
      sayfa_boyutu: s.sayfa_boyutu || 'A4',
      yonelim:      s.yonelim     || 'dikey',
      renkli:       !!s.renkli,
      renk_zemin:   s.renk_zemin  || '#1a4a7a',
      renk_yazi:    s.renk_yazi   || '#ffffff',
      renk_cizgi:   s.renk_cizgi  || '#555555',
      alan_sirasi:  s.alan_sirasi || emptyForm().alan_sirasi,
    });
    setHata(''); setModal(true);
  };

  const kaydet = async () => {
    if (!form.ad.trim()) { setHata('Şablon adı zorunludur'); return; }
    setYukleniyor(true); setHata('');
    try {
      if (secili) {
        await axios.put(`${API}/api/bilgi-notu/sablonlar/${secili.id}`, form, { headers: tkn() });
        setMesaj('Şablon güncellendi');
      } else {
        await axios.post(`${API}/api/bilgi-notu/sablonlar`, form, { headers: tkn() });
        setMesaj('Şablon eklendi');
      }
      setModal(false); yukle();
    } catch (e: any) {
      setHata(e.response?.data?.detail || 'Hata');
    } finally { setYukleniyor(false); }
  };

  const sil = async (s: any) => {
    if (!window.confirm(`"${s.ad}" şablonunu silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/api/bilgi-notu/sablonlar/${s.id}`, { headers: tkn() });
      setMesaj('Silindi'); yukle();
    } catch (e: any) { setHata(e.response?.data?.detail || 'Hata'); }
  };

  // Alan toggle — zorunlu alanlar değiştirilemez; sıralama sabit tutulur
  const alanToggle = (key: string) => {
    const alan = [...TABLO_ALANLARI, ...METIN_ALANLARI].find(a => a.key === key);
    if ((alan as any)?.zorunlu) return;
    setForm((f: any) => {
      const mevcut: string[] = f.alan_sirasi || [];
      const yeni = mevcut.includes(key)
        ? mevcut.filter((k: string) => k !== key)
        : [...mevcut, key];
      // Sabit sıra: önce tablo, sonra metin
      const sirali = [
        ...TABLO_ALANLARI.filter(a => a.zorunlu || yeni.includes(a.key)).map(a => a.key),
        ...METIN_ALANLARI.filter(a => yeni.includes(a.key)).map(a => a.key),
      ];
      return { ...f, alan_sirasi: sirali };
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#1a4a7a' }}>📋 Bilgi Notu Şablonları</h2>
        <button onClick={yeniAc}
          style={{ background: '#1a4a7a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600 }}>
          + Yeni Şablon
        </button>
      </div>

      {hata  && <div style={{ background: '#fdecea', color: '#c0392b', padding: '10px', borderRadius: 6, marginBottom: 12 }}>{hata}</div>}
      {mesaj && <div style={{ background: '#eafaf1', color: '#1e8449', padding: '10px', borderRadius: 6, marginBottom: 12 }}>{mesaj}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
        {sablonlar.map(s => (
          <SablonKarti key={s.id} s={s}
            onDuzenle={duzenleAc} onSil={sil} onOnizle={setOnizleme} />
        ))}
      </div>

      {/* Standalone önizleme modalı (listeden "Önizle" butonundan açılır) */}
      {onizlemeItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onClick={() => setOnizleme(null)}>
          <div style={{ background: '#dde', borderRadius: 10, padding: 28, maxWidth: '90vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, color: '#333' }}>{onizlemeItem.ad}</span>
              <button onClick={() => setOnizleme(null)}
                style={{ background: 'none', border: '1px solid #aaa', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>✕</button>
            </div>
            <SayfaOnizleme form={onizlemeItem} maxW={560} />
          </div>
        </div>
      )}

      {/* Düzenleme / ekleme modalı */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, width: 1060, maxWidth: '96vw', maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'row' }}>

            {/* Sol: Form */}
            <div style={{ flex: '0 0 460px', padding: 28, borderRight: '1px solid #eee', overflowY: 'auto', maxHeight: '94vh' }}>
              <h3 style={{ margin: '0 0 20px', color: '#1a4a7a' }}>{secili ? 'Şablonu Düzenle' : 'Yeni Şablon'}</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Ad */}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Şablon Adı *</label>
                  <input type="text" value={form.ad}
                    onChange={e => setForm((f: any) => ({ ...f, ad: e.target.value }))}
                    placeholder="Örn: Standart A4 Bilgi Notu"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }} />
                </div>

                {/* Açıklama */}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Açıklama</label>
                  <input type="text" value={form.aciklama}
                    onChange={e => setForm((f: any) => ({ ...f, aciklama: e.target.value }))}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }} />
                </div>

                {/* Boyut + Yönelim */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Kağıt</label>
                    <select value={form.sayfa_boyutu}
                      onChange={e => setForm((f: any) => ({ ...f, sayfa_boyutu: e.target.value }))}
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}>
                      <option value="A4">A4</option>
                      <option value="A5">A5</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Yönelim</label>
                    <select value={form.yonelim}
                      onChange={e => setForm((f: any) => ({ ...f, yonelim: e.target.value }))}
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}>
                      <option value="dikey">Dikey</option>
                      <option value="yatay">Yatay</option>
                    </select>
                  </div>
                </div>

                {/* Renk modu */}
                <div style={{ background: form.renkli ? '#fff8e1' : '#f5f5f5', border: `1px solid ${form.renkli ? '#ffe082' : '#e0e0e0'}`, borderRadius: 8, padding: '12px 14px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: form.renkli ? 14 : 0 }}>
                    <input type="checkbox" checked={!!form.renkli}
                      onChange={e => setForm((f: any) => ({ ...f, renkli: e.target.checked }))}
                      style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>🎨 Renkli şablon</span>
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>
                      {form.renkli ? 'Renkleri ayrı ayrı seçin' : 'Varsayılan: beyaz zemin, siyah yazı, gri çizgi'}
                    </span>
                  </label>

                  {form.renkli && (
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Arka Plan</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="color" value={form.renk_zemin || '#1a4a7a'}
                            onChange={e => setForm((f: any) => ({ ...f, renk_zemin: e.target.value }))}
                            style={{ padding: 2, height: 34, width: 50, borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer' }} />
                          <span style={{ fontSize: 11, color: '#666' }}>{form.renk_zemin || '#1a4a7a'}</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>etiket sütunu</div>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Yazı Rengi</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="color" value={form.renk_yazi || '#ffffff'}
                            onChange={e => setForm((f: any) => ({ ...f, renk_yazi: e.target.value }))}
                            style={{ padding: 2, height: 34, width: 50, borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer' }} />
                          <span style={{ fontSize: 11, color: '#666' }}>{form.renk_yazi || '#ffffff'}</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>etiket metni</div>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Çizgi Rengi</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="color" value={form.renk_cizgi || '#555555'}
                            onChange={e => setForm((f: any) => ({ ...f, renk_cizgi: e.target.value }))}
                            style={{ padding: 2, height: 34, width: 50, borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer' }} />
                          <span style={{ fontSize: 11, color: '#666' }}>{form.renk_cizgi || '#555555'}</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>kenarlık / ayırıcı</div>
                      </div>
                      {/* Önizleme şeridi */}
                      <div style={{ width: '100%', display: 'flex', gap: 0, borderRadius: 4, overflow: 'hidden', marginTop: 4, border: `1px solid ${form.renk_cizgi || '#555'}` }}>
                        <div style={{ background: form.renk_zemin || '#1a4a7a', color: form.renk_yazi || '#fff', padding: '6px 10px', fontSize: 11, fontWeight: 700, minWidth: 80 }}>
                          ALAN ADI
                        </div>
                        <div style={{ background: '#fff', color: '#444', padding: '6px 10px', fontSize: 11, flex: 1 }}>
                          Alan değeri örneği
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tablo alanları */}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Tablo Satırları
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 6 }}>(belgenin üst tablosunda görünür)</span>
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {TABLO_ALANLARI.map(alan => {
                      const secili2 = alan.zorunlu || (form.alan_sirasi || []).includes(alan.key);
                      return (
                        <label key={alan.key} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '7px 10px',
                          background: secili2 ? '#eef4fb' : '#fafafa',
                          borderRadius: 6, cursor: alan.zorunlu ? 'default' : 'pointer',
                          border: `1px solid ${secili2 ? '#bad4f0' : '#eee'}`,
                        }}>
                          <input type="checkbox" checked={secili2} disabled={alan.zorunlu}
                            onChange={() => alanToggle(alan.key)}
                            style={{ width: 15, height: 15 }} />
                          <span style={{ fontSize: 13, color: secili2 ? '#1a4a7a' : '#999' }}>
                            {alan.label}
                            {alan.zorunlu && <span style={{ fontSize: 10, color: '#c0392b', marginLeft: 6 }}>zorunlu</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Metin alanları */}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Metin Alanları
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 6 }}>(tablonun altında serbest metin)</span>
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {METIN_ALANLARI.map(alan => {
                      const secili2 = (form.alan_sirasi || []).includes(alan.key);
                      return (
                        <label key={alan.key} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '7px 10px',
                          background: secili2 ? '#eef4fb' : '#fafafa',
                          borderRadius: 6, cursor: 'pointer',
                          border: `1px solid ${secili2 ? '#bad4f0' : '#eee'}`,
                        }}>
                          <input type="checkbox" checked={secili2}
                            onChange={() => alanToggle(alan.key)}
                            style={{ width: 15, height: 15 }} />
                          <span style={{ fontSize: 13, color: secili2 ? '#1a4a7a' : '#999' }}>{alan.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {hata && <div style={{ color: '#c0392b', fontSize: 13, marginTop: 10 }}>{hata}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button onClick={() => setModal(false)}
                  style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: 6, background: '#f5f5f5', cursor: 'pointer' }}>
                  İptal
                </button>
                <button onClick={kaydet} disabled={yukleniyor}
                  style={{ flex: 2, padding: '10px', background: '#1a4a7a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                  {yukleniyor ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>

            {/* Sağ: Canlı önizleme */}
            <div style={{ flex: 1, background: '#e0e4ea', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '24px 16px', overflowY: 'auto', maxHeight: '94vh' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Canlı Önizleme
              </div>
              <SayfaOnizleme form={form} maxW={520} />
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
